import { StyleSheet, View } from 'react-native';
import { Body, Caption, Label } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { parseSummary } from '../utils/summary-parser';

interface StructuredSummaryProps {
  summary: string | null | undefined;
}

export function StructuredSummary({ summary }: StructuredSummaryProps) {
  const { t } = useTranslation();

  if (!summary || !summary.trim()) return null;

  const structured = parseSummary(summary);

  if (!structured) {
    return (
      <View
        style={styles.fallback}
        accessible
        accessibilityLabel={t('voiceAgents.summary.callSummary')}
      >
        <Body style={styles.text}>{summary}</Body>
      </View>
    );
  }

  const { keyPoints, objections, nextStep } = structured;

  return (
    <View
      style={styles.root}
      accessible
      accessibilityLabel={t('voiceAgents.summary.structuredSummary')}
    >
      {keyPoints.length > 0 ? (
        <View
          style={styles.section}
          accessible
          accessibilityLabel={t('voiceAgents.summary.keyPoints')}
        >
          <Label style={styles.heading}>{t('voiceAgents.summary.keyPoints')}</Label>
          {keyPoints.map((kp, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static parsed summary list
            <View key={`kp-${i}`} style={styles.bulletRow}>
              <Caption style={styles.bullet}>•</Caption>
              <Body style={styles.itemText}>{kp}</Body>
            </View>
          ))}
        </View>
      ) : null}

      {objections.length > 0 ? (
        <View
          style={styles.section}
          accessible
          accessibilityLabel={t('voiceAgents.summary.objections')}
        >
          <Label style={[styles.heading, styles.objectionsHeading]}>
            {t('voiceAgents.summary.objections')}
          </Label>
          {objections.map((ob, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static parsed summary list
            <View key={`ob-${i}`} style={styles.bulletRow}>
              <Caption style={styles.bullet}>•</Caption>
              <Body style={styles.itemText}>{ob}</Body>
            </View>
          ))}
        </View>
      ) : null}

      {nextStep ? (
        <View
          style={styles.section}
          accessible
          accessibilityLabel={t('voiceAgents.summary.nextStep')}
        >
          <Label style={[styles.heading, styles.nextStepHeading]}>
            {t('voiceAgents.summary.nextStep')}
          </Label>
          <Body style={styles.itemText}>{nextStep}</Body>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: 16,
  },
  fallback: {
    padding: 4,
  },
  section: {
    gap: 6,
  },
  heading: {
    color: colors.primary.main,
    fontWeight: '600',
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  objectionsHeading: {
    color: colors.status.warning?.main ?? colors.primary.main,
  },
  nextStepHeading: {
    color: colors.status.success?.main ?? colors.primary.main,
  },
  bulletRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
  bullet: {
    color: colors.text.tertiary,
    lineHeight: 20,
  },
  itemText: {
    color: colors.text.body,
    flex: 1,
    lineHeight: 22,
  },
  text: {
    color: colors.text.body,
    lineHeight: 22,
  },
});
